// components/layout/Header.js

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../i18n/translations';
import { useApp } from '../../context/AppContext';
import { PersistenceService } from '../../services/persistence';

const Header = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { language, toggleLanguage } = useLanguage();
    const { t } = useTranslation(language);
    const [hoveredItem, setHoveredItem] = useState(null);
    const { isAuthenticated, user, userProfile, login, logout } = useApp();
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
    const userMenuRef = useRef(null);

    useEffect(() => {
        const handler = (event) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setIsUserMenuOpen(false);
            }
        };
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, []);

    const menuItems = [
        { 
            path: '/home', 
            icon: `${process.env.PUBLIC_URL}/img/home.svg`, 
            title: t('nav.home'),
            alt: 'Home'
        },
        { 
            path: '/similar', 
            icon: `${process.env.PUBLIC_URL}/img/similar.svg`, 
            title: t('nav.similar'),
            state: { type: 'similar' },
            alt: 'Similar'
        },
        { 
            path: '/daily-discovery', 
            icon: `${process.env.PUBLIC_URL}/img/daily.svg`, 
            title: t('nav.daily'),
            state: { type: 'daily-discovery' },
            alt: 'Daily'
        },
        { 
            path: '/random', 
            icon: `${process.env.PUBLIC_URL}/img/random.svg`, 
            title: t('nav.random'),
            state: { type: 'random' },
            alt: 'Random'
        }
    ];

    const isActive = (path) => {
        // Daily Discovery è attivo anche quando sei sulle schede degli artisti
        if (path === '/daily-discovery') {
            return location.pathname === '/daily-discovery' || 
                   location.pathname === '/daily-discovery-artists';
        }
        return location.pathname === path;
    };

    return (
        <header className="header">
            <div className="header-content" style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '0 10px'
            }}>
                {/* Logo e Language Toggle */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button
                        onClick={() => navigate('/')}
                        className="logo"
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            textDecoration: 'none'
                        }}
                        title={t('nav.home')}
                    >
                        <img src={`${process.env.PUBLIC_URL}/img/logo.svg`} alt="Foundly" height="28" />
                    </button>
                    
                    {/* Language Toggle */}
                    <button
                        onClick={toggleLanguage}
                        style={{
                            background: 'rgba(255,255,255,0.1)',
                            border: '1px solid rgba(255,255,255,0.2)',
                            borderRadius: '6px',
                            padding: '4px 8px',
                            color: 'rgba(255,255,255,0.9)',
                            fontSize: '11px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            textTransform: 'uppercase'
                        }}
                        title={language === 'it' ? 'Switch to English' : 'Passa all\'italiano'}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.2)';
                        }}
                    >
                        {language === 'it' ? 'IT' : 'EN'}
                    </button>
                </div>

                {/* Menu Icons */}
                <nav style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    zIndex: 1000,
                    position: 'relative'
                }}>
                    {menuItems.map(item => {
                        const active = isActive(item.path);
                        const isHovered = hoveredItem === item.path;
                        
                        return (
                            <button
                                key={item.path}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (item.path === '/daily-discovery') {
                                        const saved = PersistenceService.getDailyState();
                                        if (saved?.items?.length && saved.activeIndex != null) {
                                            navigate('/daily-discovery-artists', {
                                                state: {
                                                    type: 'daily-discovery-artists',
                                                    items: saved.items,
                                                    query: saved.query || '',
                                                    discoveryId: saved.discoveryId,
                                                    discoveryData: saved.discoveryData || {},
                                                    savedIndex: saved.activeIndex,
                                                }
                                            });
                                            return;
                                        }
                                    }
                                    if (item.state) {
                                        navigate(item.path, { 
                                            state: { 
                                                ...item.state, 
                                                forceReset: true,
                                                timestamp: Date.now()
                                            } 
                                        });
                                    } else {
                                        navigate(item.path);
                                    }
                                }}
                                onMouseEnter={() => setHoveredItem(item.path)}
                                onMouseLeave={() => setHoveredItem(null)}
                                style={{
                                    padding: '6px 8px',
                                    border: 'none',
                                    outline: 'none',
                                    background: 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    zIndex: 1000,
                                    position: 'relative'
                                }}
                                onFocus={(e) => e.currentTarget.style.outline = 'none'}
                                title={item.title}
                            >
                                <img 
                                    src={item.icon}
                                    alt={item.alt}
                                    style={{
                                        width: '22px',
                                        height: '22px',
                                        opacity: active ? 1 : (isHovered ? 0.8 : 0.5),
                                        filter: 'invert(1) brightness(1.2)',
                                        transition: 'opacity 0.2s ease',
                                        pointerEvents: 'none'
                                    }}
                                />
                            </button>
                        );
                    })}

                    <div className="auth-container" ref={userMenuRef}>
                        {isAuthenticated ? (
                            <div className="user-menu">
                                <button
                                    className="user-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsUserMenuOpen((prev) => !prev);
                                    }}
                                    title={userProfile?.display_name || user?.email || 'Account'}
                                >
                                    {userProfile?.images?.[0]?.url || user?.user_metadata?.picture ? (
                                        <img
                                            src={userProfile?.images?.[0]?.url || user?.user_metadata?.picture}
                                            alt="avatar"
                                            className="user-avatar"
                                        />
                                    ) : (
                                        <div className="avatar-placeholder">
                                            {(userProfile?.display_name || user?.user_metadata?.full_name || 'U')
                                                .slice(0, 1)
                                                .toUpperCase()}
                                        </div>
                                    )}
                                </button>
                                {isUserMenuOpen && (
                                    <div className="dropdown-menu">
                                        <button
                                            className="dropdown-item"
                                            onClick={() => {
                                                setIsUserMenuOpen(false);
                                                navigate('/dashboard');
                                            }}
                                        >
                                            <span>Dashboard</span>
                                        </button>
                                        <button
                                            className="dropdown-item"
                                            onClick={() => {
                                                setIsUserMenuOpen(false);
                                                navigate('/favorites');
                                            }}
                                        >
                                            <span>Preferiti</span>
                                        </button>
                                        <div className="dropdown-divider" />
                                        <button
                                            className="dropdown-item"
                                            onClick={async () => {
                                                setIsUserMenuOpen(false);
                                                await logout();
                                                navigate('/home', { replace: true });
                                            }}
                                        >
                                            <span>Logout</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <button className="login-icon-btn" onClick={login} title="Login">
                                <img
                                    src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                                    alt="G"
                                    style={{ width: 18, height: 18 }}
                                />
                            </button>
                        )}
                    </div>
                </nav>
            </div>
        </header>
    );
};

export default Header;